import React from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Product } from "@/data/products";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useCart } from "@/context/CartContext";
import { useToast } from "@/hooks/use-toast";
import { useCurrency } from "@/context/CurrencyContext";
import ProductImage from "./ProductImage";

interface ProductCardProps {
  product: Product;
  className?: string;
  delay?: number;
  flagBackgroundColor?: string;
  variant?: 'default' | 'featured';
}

// Helper function to get stock status - matching admin portal logic
const getStockStatus = (product: Product) => {
  const stockQuantity = product.stockQuantity || 0;
  const lowStockThreshold = product.lowStockThreshold || 10;
  
  if (stockQuantity === 0) {
    return { status: 'Out of Stock', color: 'bg-red-100 text-red-800', isOutOfStock: true };
  }
  if (stockQuantity <= lowStockThreshold) {
    return { status: 'Low Stock', color: 'bg-yellow-100 text-yellow-800', isOutOfStock: false };
  }
  return { status: 'In Stock', color: 'bg-green-100 text-green-800', isOutOfStock: false };
};

const ProductCard = ({ product, className, delay = 0, flagBackgroundColor = 'bg-gray-100', variant = 'default' }: ProductCardProps) => {
  const { addToCart } = useCart();
  const { toast } = useToast();
  const { formatPrice, selectedCurrency, getConvertedItemPrice, formatDisplayAmount } = useCurrency();
  
  // Create a truncated description for the card
  const productDescription = product.description 
    ? product.description.substring(0, 100) + '...' 
    : 'Jamaican authentic product';

  const stockStatus = getStockStatus(product);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent navigation to product detail
    e.stopPropagation();
    
    if (stockStatus.isOutOfStock) {
      toast({
        title: "Out of Stock",
        description: `${product.name} is currently out of stock.`,
        variant: "destructive",
      });
      return;
    }
    
    addToCart(product, 1);
    
    toast({
      title: "Added to Cart",
      description: `${product.name} has been added to your cart.`,
      variant: "default",
    });
  };

  return (
    <Card 
      className={cn(
        "overflow-hidden border-none shadow-md hover:shadow-lg transition-all duration-300 animate-fade-in",
        product.onSale && product.onSalePrice && product.onSalePrice < product.price 
          ? "ring-2 ring-jamaica-yellow/50 hover:ring-jamaica-yellow/70 shadow-jamaica-yellow/20" 
          : "",
        className
      )}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className={cn("relative overflow-hidden aspect-square", flagBackgroundColor)}>
        <Link to={`/product/${product.id}`} className="block h-full touch-manipulation">
          <ProductImage
            src={product.image}
            alt={product.name}
            className="transform transition-transform duration-500 group-hover:scale-105"
            aspectRatio="square"
          />
          
          {/* Sale corner ribbon - only for sale items */}
          {!stockStatus.isOutOfStock && product.onSale && product.onSalePrice && product.onSalePrice < product.price && (
            <div className="absolute top-0 right-0 z-20">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-jamaica-yellow to-jamaica-yellow/90 transform rotate-45 translate-x-8 -translate-y-8"></div>
                <div className="absolute top-2 right-2 text-jamaica-black text-xs font-bold transform rotate-45">
                  %
                </div>
              </div>
            </div>
          )}

          {/* Badges overlay */}
          <div className="absolute top-2 left-2 sm:top-3 sm:left-3 flex flex-wrap gap-1 sm:gap-2 z-10">
            {stockStatus.isOutOfStock && (
              <span className="bg-red-100 text-red-800 text-xs font-semibold px-2 py-0.5 sm:px-3 sm:py-1 rounded-full backdrop-blur-sm shadow-sm border border-red-200">
                OUT OF STOCK
              </span>
            )}
            {!stockStatus.isOutOfStock && product.isNew && (
              <span className="bg-jamaica-green text-white text-xs font-semibold px-2 py-0.5 sm:px-3 sm:py-1 rounded-full backdrop-blur-sm shadow-sm">
                NEW
              </span>
            )}
            {!stockStatus.isOutOfStock && product.isBestSeller && (
              <span className="bg-jamaica-yellow text-jamaica-black text-xs font-semibold px-2 py-0.5 sm:px-3 sm:py-1 rounded-full backdrop-blur-sm shadow-sm">
                BEST SELLER
              </span>
            )}
            {!stockStatus.isOutOfStock && product.onSale && (
              <span className="relative bg-gradient-to-r from-jamaica-black to-jamaica-black text-jamaica-yellow text-xs font-bold px-2 py-0.5 sm:px-3 sm:py-1 rounded-full backdrop-blur-sm shadow-lg border-2 border-white/50 animate-pulse">
                <span className="relative z-10">ON SALE</span>
                <div className="absolute inset-0 bg-gradient-to-r from-jamaica-yellow/30 to-jamaica-yellow/30 rounded-full opacity-50 animate-ping"></div>
              </span>
            )}
          </div>
          
          {/* Hover overlay - hidden on touch devices, visible on hover for desktop */}
          <div className="absolute inset-0 bg-black/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100 items-center justify-center hidden sm:flex">
            <span className="text-white font-medium px-4 py-2 rounded-full bg-jamaica-green/80 backdrop-blur-sm transform translate-y-4 transition-transform duration-300 group-hover:translate-y-0">
              View Details
            </span>
          </div>
        </Link>
      </div>
      
      <CardContent className="p-3 sm:p-5">
        <h3 className="text-sm sm:text-lg font-medium text-gray-800 leading-snug">
          <Link 
            to={`/product/${product.id}`} 
            className="hover:text-jamaica-green transition-colors touch-manipulation"
          >
            {product.name}
          </Link>
        </h3>
        
        {/* Brand and Weight Info */}
        <div className="flex items-center gap-2 mt-1 mb-2">
          {product.brand && (
            <span className="inline-flex items-center text-xs sm:text-xs font-medium text-jamaica-green bg-jamaica-green/10 px-1.5 py-0.5 rounded-full border border-jamaica-green/20 leading-none">
              {product.brand}
            </span>
          )}
          {product.netWeight && product.weightUnit && (
            <span className="inline-flex items-center text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
              {product.netWeight}{product.weightUnit}
            </span>
          )}
        </div>
        
        <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 min-h-[2rem] sm:min-h-[2.5rem] leading-relaxed">
          {productDescription}
        </p>
      </CardContent>
      
      <CardFooter className="flex justify-between items-center p-3 sm:p-5 pt-2 sm:pt-3 border-t border-gray-100 gap-2 min-h-[5rem] sm:min-h-[6rem]">
        <div className="flex-1 min-w-0 flex items-center h-12 sm:h-14">
          {product.onSale && product.onSalePrice && product.onSalePrice < product.price ? (
            <div className="relative w-full flex flex-col justify-center">
              {/* Mobile layout - compact and sleek */}
              <div className="sm:hidden">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="relative">
                    <span className={`font-bold text-jamaica-yellow leading-tight relative z-10 ${
                      variant === 'featured' ? 'text-[7px]' : 'text-xs'
                    }`}>
                      {formatPrice(product.onSalePrice)} {selectedCurrency}
                    </span>
                    {/* Subtle glow effect for sale price */}
                    <div className={`absolute inset-0 font-bold text-jamaica-yellow/40 blur-sm opacity-30 ${
                      variant === 'featured' ? 'text-[7px]' : 'text-xs'
                    }`}>
                      {formatPrice(product.onSalePrice)} {selectedCurrency}
                    </div>
                  </div>
                  <div className="relative">
                    <span className="text-[10px] text-gray-500 line-through relative z-10">
                      {formatPrice(product.price)}
                    </span>
                    {/* Crossed out line with enhanced styling */}
                    <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-jamaica-yellow transform -rotate-12 opacity-60"></div>
                  </div>
                </div>
                {/* Compact savings indicator */}
                <div className="flex justify-start">
                  <span className="inline-flex items-center text-[9px] font-semibold text-jamaica-yellow bg-jamaica-black px-1.5 py-0.5 rounded-full border border-jamaica-yellow/50">
                    Save {formatDisplayAmount(getConvertedItemPrice(product.price) - getConvertedItemPrice(product.onSalePrice))}
                  </span>
                </div>
              </div>

              {/* Desktop layout - unchanged */}
              <div className="hidden sm:block">
                <div className="flex flex-row items-center gap-2">
                  <div className="relative">
                    <span className={`font-bold text-jamaica-yellow leading-tight relative z-10 ${
                      variant === 'featured' ? 'text-xs' : 'text-base'
                    }`}>
                      {formatPrice(product.onSalePrice)} {selectedCurrency}
                    </span>
                    {/* Subtle glow effect for sale price */}
                    <div className={`absolute inset-0 font-bold text-jamaica-yellow/40 blur-sm opacity-30 ${
                      variant === 'featured' ? 'text-xs' : 'text-base'
                    }`}>
                      {formatPrice(product.onSalePrice)} {selectedCurrency}
                    </div>
                  </div>
                  <div className="relative">
                    <span className="text-sm text-gray-500 line-through relative z-10">
                      {formatPrice(product.price)}
                    </span>
                    {/* Crossed out line with enhanced styling */}
                    <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-jamaica-yellow transform -rotate-12 opacity-60"></div>
                  </div>
                </div>
                {/* Savings indicator */}
                <div className="mt-1">
                  <span className="inline-flex items-center text-xs font-semibold text-jamaica-yellow bg-jamaica-black px-2 py-0.5 rounded-full border border-jamaica-yellow/50">
                    Save {formatDisplayAmount(getConvertedItemPrice(product.price) - getConvertedItemPrice(product.onSalePrice))}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="w-full flex items-center justify-start">
              <span className={`font-bold text-jamaica-green leading-tight ${
                variant === 'featured' ? 'text-xs sm:text-sm' : 'text-base sm:text-lg'
              }`}>{formatPrice(product.price)} {selectedCurrency}</span>
            </div>
          )}
        </div>
        <Button 
          size="sm" 
          className={cn(
            "shadow-sm rounded-full touch-manipulation flex-shrink-0",
            "h-8 w-8 p-0 sm:h-auto sm:w-auto sm:min-h-[32px] sm:min-w-auto sm:px-3 sm:py-1.5",
            stockStatus.isOutOfStock 
              ? "bg-gray-400 text-gray-600 cursor-not-allowed hover:bg-gray-400" 
              : "bg-jamaica-green text-white hover:bg-jamaica-green/90 hover:scale-105 sm:hover:scale-100 transition-transform duration-200"
          )}
          onClick={handleAddToCart}
          disabled={stockStatus.isOutOfStock}
          title={stockStatus.isOutOfStock ? "Out of Stock" : "Add to Cart"}
        >
          {/* Mobile view - icon only with plus badge */}
          <div className="sm:hidden">
            {stockStatus.isOutOfStock ? (
              <X className="h-4 w-4" />
            ) : (
              <div className="relative">
                <ShoppingCart className="h-3.5 w-3.5" />
                <div className="absolute -top-3 -right-3 bg-jamaica-yellow text-jamaica-black h-4 w-4 flex items-center justify-center rounded-full p-0.5">
                  <Plus className="h-2 w-2" />
                </div>
              </div>
            )}
          </div>
          
          {/* Desktop view - text with icon */}
          <div className="hidden sm:flex sm:items-center sm:gap-2">
            <ShoppingCart className="h-4 w-4" />
            <span className="text-sm">
              {stockStatus.isOutOfStock ? "Out of Stock" : "Add"}
            </span>
          </div>
        </Button>
      </CardFooter>
    </Card>
  );
};

export default ProductCard;
